import React, { useState } from "react";
import { Button, TextField } from "@mui/material";
import { toast } from "react-toastify";
import { HTTP_METHODS, invokeApi } from "../../utils/http-service";
import { FINANCE_V2_HOSTNAME } from "../../../common-utilities/utils/end-points";
import { FINANCE_REST_URLS } from "../../../common-utilities/utils/end-points/finance-v2";
import CustomModal from "../../../common-utilities/core/modal";
import Select from "../../../common-utilities/core/select-component";

// Shared Reconsider action (trigger button + modal) for PR/PO/GRN/Invoice-data/Vendor Mapping.
// Lets the approver optionally pick an earlier already-approved stage to send the document back
// to, instead of it always resetting to the creator. Falls back to the plain remarks-only modal
// (today's behavior) when the approval hasn't passed any stage yet.
// Sentinel for "send back to the creator": omits targetStageNumber so the backend runs its
// creator-reset path (pendingWith = document creator, status = reconsider).
const CREATOR_TARGET = "CREATOR";

const ReconsiderAction = ({
  approvalDetails,
  title,
  buttonLabel = "Reconsider",
  submitLabel,
  disabled = false,
  buttonProps = {},
  onClose,
  onSuccess,
}) => {
  const [open, setOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [selectedStageNumber, setSelectedStageNumber] = useState("");

  const eligibleStages = (approvalDetails?.stages || [])
    .filter((s, idx, arr) => s.status === "approved" && idx !== arr.length - 1)
    .map((s) => ({
      value: s.StageNumber,
      label: `${s.stageRoleName || s.stageName}${s.approverName ? " - " + s.approverName : ""}`,
    }));
  const creatorName = approvalDetails?.creatorName || approvalDetails?.document?.creatorName;
  const stageOptions = eligibleStages.length
    ? [{ value: CREATOR_TARGET, label: `Creator${creatorName ? " - " + creatorName : ""}` }, ...eligibleStages]
    : [];

  const resetState = () => {
    setComment("");
    setSelectedStageNumber("");
  };

  const handleClose = () => {
    setOpen(false);
    resetState();
    onClose?.();
  };

  const onReconsider = () => {
    invokeApi(
      HTTP_METHODS.POST,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.RECONSIDER}`,
      null,
      {
        comment,
        id: approvalDetails.id,
        ...(selectedStageNumber !== "" && selectedStageNumber !== CREATOR_TARGET
          ? { targetStageNumber: selectedStageNumber }
          : {}),
      }
    )
      .then((response) => {
        if (response?.code) {
          toast.error(response.message || "Failed to reconsider");
        } else {
          setOpen(false);
          resetState();
          onSuccess?.();
        }
      })
      .catch(() => {});
  };

  return (
    <>
      <Button variant="outlined" color="warning" {...buttonProps} disabled={disabled} onClick={() => setOpen(true)}>
        {buttonLabel}
      </Button>
      {open && (
        <CustomModal title={title} disabledOutsideClick={true} onClose={handleClose}>
          <div className="finance-v2-b-modal-form-container">
            {stageOptions.length > 0 && (
              <Select
                label="Send back to stage"
                value={selectedStageNumber}
                onChange={(e) => setSelectedStageNumber(e.target.value)}
                options={stageOptions}
              />
            )}
            <TextField
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              size="small"
              name="comment"
              label="Comment"
              fullWidth
            />
            <Button
              color="warning"
              variant="outlined"
              onClick={onReconsider}
              fullWidth
              disabled={!comment || (stageOptions.length > 0 && !selectedStageNumber)}
            >
              {submitLabel || buttonLabel}
            </Button>
          </div>
        </CustomModal>
      )}
    </>
  );
};

export default ReconsiderAction;
